import React, { Suspense } from "react"
import { RouterProvider, useNavigate } from "react-router-dom"
import router from "@/router/config"
import { useDispatch } from "react-redux"
import { useEffectOnce, useLocalStorage, useReadLocalStorage } from "usehooks-ts"
import { Worker } from "@react-pdf-viewer/core"
import { useHotkeys } from "react-hotkeys-hook"
import { withAITracking } from "@microsoft/applicationinsights-react-js"
import {
    setAnswerGenerationPromptTemplate,
    setAssistantType,
    setCitationsHighlightMode,
    setCognitiveSearchQueryGenerationPromptTemplate,
    setDocsPreselectSearchThreshold,
    setDocsSearchThreshold,
    setExcludeCategory,
    setImproveSearchQueryMode,
    setModel,
    setTokensUsageMode,
    setUserType,
    setModelProvider,
    setPdfCitationDisplayMode,
    setPreselectContexFilesMode,
    setSearchApproach,
    setSelectedDocuments,
    setShowWelcomeMessage,
    setTemperatureCount,
    setUseSuggestFollowupQuestions,
    setUseReasoning,
    setAllowGeneralKnowledge,
} from "@/slices/chatSlice"
import { SearchApproaches } from "@/api"

import "react-toastify/dist/ReactToastify.css"
import { toast, ToastContainer } from "react-toastify"

import { CssBaseline, ThemeProvider } from "@mui/material"
import theme from "./theme"
import { ModalProvider } from "@/components/ModalProvider"
import axios from "axios"
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react"
import unauthenticatedRouter from "@/router/unauthenticatedRouter"
import LoginSuccessListener from "@/components/LoginSuccessListener"
import { isMobile } from "@/utils/mobileDetect"
import { setWorkflowJSON } from "@/slices/workflowsSlice"
import {
    getProviderOptions,
    DEFAULT_PERSONAL_SETTINGS,
    migratePersonalSettings,
} from "@/components/Settings/useSettings"
import { getValidAccessToken } from "@/utils/accessToken"
import { AuthAxiosRequestConfig, getAuthRecoveryToken } from "@/utils/authAxios"
import {
    createSessionExpiredError,
    handleExhaustedUnauthorized,
    recoverFromUnauthorized,
    setRequestAuthorization,
    shouldForceReauthOn401,
} from "@/utils/authSessionRecovery"
import ProactiveTokenRefresh from "@/components/ProactiveTokenRefresh/ProactiveTokenRefresh"
import { InterfaceLanguageSync } from "@/components/InterfaceLanguageSync"
import "./i18n"
import { t } from "i18next"
import { withTranslation } from "react-i18next"
import { reactPlugin } from "@/telemetry/appInsights"
import { IS_TAZ } from "@/utils/brand"
import { PreviewSessionBoundary } from "@/components/PreviewSessionBoundary/PreviewSessionBoundary"

axios.interceptors.request.use(
    async config => {
        const recoveryToken = getAuthRecoveryToken(config)
        if (recoveryToken) {
            setRequestAuthorization(config, recoveryToken)
            return config
        }

        const accessToken = await getValidAccessToken()
        if (accessToken) {
            setRequestAuthorization(config, accessToken)
        }

        return config
    },
    error => Promise.reject(error)
)

const waitForAuthRedirect = (): Promise<never> => new Promise(() => {})

const rejectUnlessRedirectStarted = async (redirectStarted: boolean): Promise<never> => {
    if (redirectStarted) {
        return waitForAuthRedirect()
    }
    return Promise.reject(createSessionExpiredError())
}

axios.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config as AuthAxiosRequestConfig | undefined
        const currentPath = window.location.href.split("#")[1] || "/"
        const isUnauthorized =
            error?.response?.status === 401 &&
            originalRequest &&
            !["/terms-of-service", "/privacy-policy"].includes(currentPath)

        if (!isUnauthorized) {
            return Promise.reject(error)
        }

        const authOptions = { isMobileDevice: isMobile() }

        if (originalRequest._retry) {
            if (!shouldForceReauthOn401(error)) {
                return Promise.reject(error)
            }
            const redirectStarted = await handleExhaustedUnauthorized(authOptions)
            return rejectUnlessRedirectStarted(redirectStarted)
        }

        const recovery = await recoverFromUnauthorized(originalRequest, authOptions)

        if (recovery.action === "redirect") {
            return rejectUnlessRedirectStarted(recovery.redirectStarted)
        }

        try {
            return await recovery.request()
        } catch (retryError) {
            if (axios.isAxiosError(retryError) && retryError.response?.status === 401) {
                if (!shouldForceReauthOn401(retryError)) {
                    return Promise.reject(retryError)
                }
                const redirectStarted = await handleExhaustedUnauthorized(authOptions)
                return rejectUnlessRedirectStarted(redirectStarted)
            }
            return Promise.reject(retryError)
        }
    }
)

function App() {
    const dispatch = useDispatch()

    const localSettingsRaw = useReadLocalStorage("personalSettings")
    const workflowSettings = useReadLocalStorage("workflowSettings")
    const selectedDocs = useReadLocalStorage("selectedDocs")
    const showTokens = useReadLocalStorage("showTokens")
    const [isShowTokens, setShowTokens] = useLocalStorage("showTokens", showTokens || false)

    useHotkeys(
        "ctrl+shift+x",
        () => {
            toast.info(t("Show tokens and diagnostics: {{state}}", { state: !isShowTokens ? "ON" : "OFF" }), {
                position: "top-center",
                autoClose: 2000,
            })
            setShowTokens(!isShowTokens)
        },
        [setShowTokens, isShowTokens]
    )
    // TODO: remove local setup after testing
    useEffectOnce(() => {
        const localSettings = migratePersonalSettings(
            (localSettingsRaw as Partial<typeof DEFAULT_PERSONAL_SETTINGS> | null) ?? undefined
        )
        dispatch(setAnswerGenerationPromptTemplate(localSettings.answerGenerationPromptTemplate))
        dispatch(
            setCognitiveSearchQueryGenerationPromptTemplate(localSettings.cognitiveSearchQueryGenerationPromptTemplate)
        )
        // @ts-ignore legacy key
        dispatch(setExcludeCategory((localSettingsRaw as any)?.useExcludeCategory || ""))
        dispatch(setUseSuggestFollowupQuestions(localSettings.useSuggestFollowupQuestions ?? true))
        dispatch(setPreselectContexFilesMode(localSettings.preselectContextFilesMode))
        const storedSearchApproach =
            localSettings.searchApproach === ("keyword" as SearchApproaches)
                ? SearchApproaches.Auto
                : localSettings.searchApproach
        dispatch(setSearchApproach(storedSearchApproach))
        dispatch(setTemperatureCount(localSettings.temperatureCount))
        dispatch(setDocsPreselectSearchThreshold(localSettings.docsPreselectSearchThreshold))
        dispatch(setDocsSearchThreshold(localSettings.docsSearchThreshold))
        dispatch(setModel(localSettings.selectedModel))
        dispatch(setAssistantType(localSettings.assistantType))
        dispatch(setSelectedDocuments(selectedDocs || []))
        dispatch(setShowWelcomeMessage(localSettings.showWelcomeMessage))

        dispatch(setPdfCitationDisplayMode(localSettings.pdfCitationDisplayMode))

        dispatch(setCitationsHighlightMode(localSettings.citationsHighlightMode))

        // @ts-ignore
        dispatch(setWorkflowJSON(workflowSettings?.workflowJSON || ""))

        const provider = getProviderOptions().find(p => p.key === "azure")?.key ?? ""
        dispatch(setModelProvider(localSettings.modelProvider || provider || ""))
        dispatch(setImproveSearchQueryMode(localSettings.improveSearchQueryMode))
        dispatch(setTokensUsageMode(localSettings.selectedTokensUsageMode))
        dispatch(setUserType(localSettings.userType))
        dispatch(setUseReasoning(IS_TAZ ? true : localSettings.useReasoning))
        dispatch(setAllowGeneralKnowledge(localSettings.allowGeneralKnowledge))
    })

    return (
        <ThemeProvider theme={theme}>
            <ModalProvider>
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                    <CssBaseline />
                    <AuthenticatedTemplate>
                        <PreviewSessionBoundary>
                            <ProactiveTokenRefresh />
                            <InterfaceLanguageSync />
                            <RouterProvider router={router} />
                        </PreviewSessionBoundary>
                    </AuthenticatedTemplate>
                    <UnauthenticatedTemplate>
                        <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
                            <RouterProvider router={unauthenticatedRouter} />
                        </Suspense>
                    </UnauthenticatedTemplate>
                    <LoginSuccessListener />
                    <ToastContainer
                        position="top-right"
                        theme="light"
                        style={{
                            zIndex: 2000,
                        }}
                        draggable
                    />
                </Worker>
            </ModalProvider>
        </ThemeProvider>
    )
}

export default withAITracking(reactPlugin, withTranslation()(App))
